import { useState, useEffect, useCallback, useMemo } from "react";
import { listTenantWorkspaceDirectoryUsers } from "@/api/tenantWorkspaceDirectoryApi";
import { listWorkspaceMemberEmails, normalizeWorkspaceEmail } from "@/api/workspacesApi";

/**
 * @param {{ open: boolean, workspaceId: string | null, workspaceName?: string, onClose: () => void, onSave: (emails: string[]) => Promise<void> }} props
 */
export default function AssignWorkspaceMembersModal({ open, workspaceId, workspaceName, onClose, onSave }) {
  const [directory, setDirectory] = useState([]);
  const [memberEmails, setMemberEmails] = useState(/** @type {Set<string>} */ (new Set()));
  const [selected, setSelected] = useState(/** @type {Set<string>} */ (new Set()));
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    (async () => {
      setFormError("");
      setSearch("");
      setBusy(true);
      try {
        const [{ data: u }, { data: m }] = await Promise.all([
          listTenantWorkspaceDirectoryUsers(),
          listWorkspaceMemberEmails({ workspaceId }),
        ]);
        if (cancelled) return;
        setDirectory(u.users);
        const mem = new Set(m.emails.map(normalizeWorkspaceEmail));
        setMemberEmails(mem);
        setSelected(new Set(mem));
      } catch (e) {
        if (!cancelled) setFormError(e instanceof Error ? e.message : "Failed to load.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter(
      (u) => u.name.toLowerCase().includes(q) || String(u.email).toLowerCase().includes(q)
    );
  }, [directory, search]);

  const toggle = useCallback((email) => {
    const key = normalizeWorkspaceEmail(email);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!workspaceId) return;
    setFormError("");
    setBusy(true);
    try {
      await onSave([...selected]);
      onClose();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }, [workspaceId, selected, onSave, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ui-card-static flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col overflow-hidden !p-0 sm:!p-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-ws-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-6 py-4">
          <div className="min-w-0">
            <h2 id="assign-ws-title" className="text-lg font-semibold tracking-tight text-slate-900">
              Assign people
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Choose who can access <span className="font-semibold text-slate-800">{workspaceName ?? "this workspace"}</span>.
              Your signed-in account always keeps access.
            </p>
          </div>
          <button type="button" onClick={onClose} className="ui-btn-icon shrink-0" aria-label="Close">
            <i className="ri-close-line text-xl" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden px-6 py-3">
          {formError ? (
            <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="relative mb-3">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 text-slate-400">
              <i className="ri-search-line text-sm" aria-hidden />
            </span>
            <input
              type="search"
              className="ui-input !pl-9"
              placeholder="Filter by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={busy}
              aria-label="Filter users"
            />
          </div>
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-100 bg-slate-50/40 scrollbar-thin">
            {busy && !directory.length ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">Loading directory…</p>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">No users match this filter.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((u) => {
                  const key = normalizeWorkspaceEmail(u.email);
                  const checked = selected.has(key);
                  const wasMember = memberEmails.has(key);
                  return (
                    <li key={u.id}>
                      <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-white">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                          checked={checked}
                          onChange={() => toggle(u.email)}
                          disabled={busy}
                        />
                        <img src={u.avatar} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-slate-200" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-900">{u.name}</span>
                          <span className="block truncate text-xs text-slate-500">{u.email}</span>
                        </span>
                        {wasMember ? (
                          <span className="shrink-0 rounded-md bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">
                            Was in
                          </span>
                        ) : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button type="button" className="ui-btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="ui-btn-primary" disabled={busy} onClick={() => void handleSave()}>
            {busy ? "Saving…" : "Save access"}
          </button>
        </div>
      </div>
    </div>
  );
}
